<template>
  <div class="s-contact-form__row s-contact-form__row--breadcrumb">
    <div class="s-contact-form__col s-contact-form__col--grey">
      <div class="s-contact-form__text">
        <div class="s-breadcrumb-target"></div>
      </div>
    </div>

    <div class="s-contact-form__col s-contact-form__col--blue">
      <div class="s-contact-form__content"></div>
    </div>
  </div>

  <div
    v-for="(title, i) in stepTitles"
    :key="i"
    class="s-contact-form__row"
    :class="[
      i === 1 && selectedSubj === '' ? 's-contact-form__row-disabled' : '',
      i === 0 ? 's-contact-form__row--first' : 's-contact-form__row--second',
    ]">
    <div class="s-contact-form__col s-contact-form__col--grey">
      <div class="s-contact-form__text">
        <div class="s-breadcrumb-target" v-if="i === 0"></div>

        <span class="s-contact-form__counter">
          {{ i + 1 }} / {{ stepTitles.length }}
        </span>
        <div class="s-contact-form__title">{{ title }}</div>
      </div>
    </div>

    <div class="s-contact-form__col s-contact-form__col--blue">
      <div v-if="i === 0" class="s-contact-form__content">
        <div class="s-contact-form__mobHeader">
          <span class="s-contact-form__counter">
            {{ i + 1 }} / {{ stepTitles.length }}
          </span>
          <div class="s-contact-form__title">{{ title }}</div>
        </div>

        <div class="s-contact-form__answers">
          <options
            :items="subjects"
            @changed-option="getSelectedSubject"></options>
        </div>
      </div>
      <div v-if="i === 1" class="s-contact-form__content">
        <div class="s-contact-form__mobHeader">
          <span class="s-contact-form__counter">
            {{ i + 1 }} / {{ stepTitles.length }}
          </span>
          <div class="s-contact-form__title">{{ title }}</div>
        </div>

        <div class="s-contact-form__form">
          <Loader v-if="isLoading" display="absolute"></Loader>

          <form
            v-show="!showResponse && !showFailResponse"
            ref="form"
            autocomplete="off"
            enctype="multipart/form-data"
            @submit.prevent="onSubmitForm">
            <div class="form-panel">
              <div class="mb-3">
                <label for="name">{{ dictionary.nameLabel }}</label>
                <input
                  id="name"
                  v-model.trim="name"
                  type="text"
                  class="form-control"
                  :placeholder="dictionary.nameLabel"
                  :class="{ 'has-error': v$.name.$error }"
                  @blur="v$.name.$touch" />

                <div v-if="v$.name.$error">
                  <div v-if="v$.name.required.$invalid" class="error">
                    {{ dictionary.nameRequired }}
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label for="surname">
                  {{ dictionary.surnameLabel }}
                </label>
                <input
                  id="surname"
                  v-model.trim="surname"
                  type="text"
                  class="form-control"
                  :placeholder="dictionary.surnameLabel"
                  :class="{ 'has-error': v$.surname.$error }"
                  @blur="v$.surname.$touch" />

                <div v-if="v$.surname.$error">
                  <div v-if="v$.surname.required.$invalid" class="error">
                    {{ dictionary.surnameRequired }}
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label for="email">{{ dictionary.emailLabel }}</label>
                <input
                  id="email"
                  v-model.trim="email"
                  type="email"
                  class="form-control"
                  :placeholder="dictionary.emailLabel"
                  :class="{ 'has-error': v$.email.$error }"
                  @blur="v$.email.$touch" />

                <div v-if="v$.email.$error">
                  <div v-if="v$.email.required.$invalid" class="error">
                    {{ dictionary.emailRequired }}
                  </div>
                  <div v-if="v$.email.email.$invalid" class="error">
                    {{ dictionary.emailRegex }}
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label for="phone">{{ dictionary.phoneLabel }}</label>
                <input
                  id="phone"
                  v-model.trim="phone"
                  type="text"
                  class="form-control"
                  :placeholder="dictionary.phoneLabel"
                  :class="{ 'has-error': v$.phone.$error }"
                  @blur="v$.phone.$touch" />

                <div v-if="v$.phone.$error">
                  <div v-if="v$.phone.required.$invalid" class="error">
                    {{ dictionary.emailRequired }}
                  </div>
                  <div v-if="v$.phone.numeric.$invalid" class="error">
                    {{ dictionary.phoneNumberNumeric }}
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label for="msg">
                  {{ dictionary.msgLabel }}
                </label>
                <textarea
                  id="msg"
                  v-model="msg"
                  type="text"
                  class="form-control"
                  :placeholder="dictionary.msgLabel"
                  :class="{ 'has-error': v$.msg.$error }"
                  @blur="v$.msg.$touch" />

                <div v-if="v$.msg.$error">
                  <div v-if="v$.msg.required.$invalid" class="error">
                    {{ dictionary.msgRequired }}
                  </div>
                </div>
              </div>

              <div class="mb-32 mt-40">
                <div class="form-check">
                  <input
                    id="terms"
                    v-model.trim="terms"
                    class="form-check-input"
                    type="checkbox"
                    :class="{ 'has-error': v$.terms.$error }"
                    @blur="v$.terms.$touch" />
                  <label class="form-check-label" for="terms">
                    <div v-html="dictionary.termsLabel"></div>
                  </label>

                  <div v-if="v$.terms.$error">
                    <div v-if="v$.terms.checked.$invalid" class="error">
                      {{ dictionary.termsRequired }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="s-contact__cta">
                  <button
                    type="submit"
                    class="btn btn-secondary"
                    :disabled="isLoading || v$.$invalid">
                    <span>{{ dictionary.buttonSubmitText }}</span>
                  </button>
                </div>
              </div>
            </div>
          </form>

          <div
            v-show="showResponse"
            class="s-contact__msg form-panel"
            :class="showResponse ? 'active' : ''">
            <span
              class="s-contact__msg__icon s-contact__msg__icon--success"></span>
            <div class="s-contact__msg__title">
              {{ dictionary.responseSuccessMesage }}
            </div>
            <div class="s-contact__msg__desc">
              {{ dictionary.responseSuccessDescription }}
            </div>
          </div>

          <div
            v-show="showFailResponse"
            class="s-contact__msg form-panel"
            :class="showFailResponse ? 'active' : ''">
            <span
              class="s-contact__msg__icon s-contact__msg__icon--fail"></span>
            <div class="s-contact__msg__title">
              {{ dictionary.responseFailMesage }}
            </div>
            <div class="s-contact__msg__desc">
              {{ dictionary.responseFailDescription }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

import Loader from './../components/common/Loader.vue'
import Options from './../components/contact/Options.vue'
import * as lib from './../../lib/lib'
import useVuelidate from '@vuelidate/core'

import { email, numeric, required } from '@vuelidate/validators'
import { v4 as uuidv4 } from 'uuid'

export default {
  components: {
    Loader,
    Options,
  },
  setup() {
    return { v$: useVuelidate() }
  },

  data() {
    return {
      // default
      componentKey: uuidv4(),
      scrollOffsetY: 100,
      isLoading: false,
      errorMsgs: [],
      showResponse: false,
      showFailResponse: false,
      // page properties
      subjects: '',
      stepTitles: [],
      selectedSubj: '',
      // form properties
      name: '',
      surname: '',
      msg: '',
      email: '',
      phone: '',
      terms: false,
      // reCaptchaResponse: '',
    }
  },

  computed: {
  
  },
  validations() {
    return {
      name: { required },
      surname: { required },
      msg: { required },
      email: { required, email },
      phone: { required, numeric },
      terms: { checked: (value) => value === true },
      // reCaptchaResponse: { required }
    }
  },

  mounted() {
    const { subjects, stepTitles } = lib.getData('contactData')
    this.subjects = subjects
    this.stepTitles = stepTitles
  },

  methods: {
    async callCaptcha() {
      /* eslint-disable no-undef */

      return new Promise((resolve, reject) => {
        const fetchAsyncData = async () => {
          try {
            const result = await grecaptcha.execute(
              this.configuration.reCaptchaPublicKey,
              {
                action: this.configuration.action,
              }
            )
            resolve(result)
          } catch (error) {
            reject(error)
          }
        }

        fetchAsyncData()
      })

      /* eslint-enable no-alert, no-console */
    },

    async onSubmitForm() {
      this.v$.$validate()

      const captchaToken = await this.callCaptcha()
      const formData = new FormData()

      if (!captchaToken) return

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      this.isLoading = true

      formData.append('name', this.name)
      formData.append('surname', this.surname)
      formData.append('email', this.email)
      formData.append('phone', this.phone)
      formData.append('subject', this.selectedSubj)
      formData.append('terms', this.terms)
      formData.append('msg', this.msg)
      formData.append('__RequestVerificationToken', this.configuration.token)
      formData.append('capctchaTokenResponse', captchaToken)

      axios
        .post(this.configuration.endpointSubmitData, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        .then((response) => {
          const { hasError, errorMessages } = response.data
          // response has error
          if (hasError) {
            this.handleError(errorMessages)
            return false
          }
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    handleSuccess() {
      this.errorMsgs = []
      this.isLoading = false
      this.showResponse = true
      this.showFailResponse = false
      this.resetForm()

      if (jQuery('.s-contact__msg.active').length) {
        this.$nextTick(() => {
          var h =
            jQuery('.s-contact__msg.active').outerHeight() +
            jQuery('.s-contact-form__row--breadcrumb').outerHeight()

          jQuery('.s-contact-form').css('height', h)
        })
      }
      this.v$.$reset()
    },

    handleError(messages) {
      this.errorMsgs = messages
      this.isLoading = this.showResponse = false
      this.showFailResponse = true

      // this.$nextTick(() => {
      //   lib.scrollToError(this.scrollOffsetY)
      // })
    },

    resetForm() {
      this.name = ''
      this.surname = ''
      this.email = ''
      this.phone = ''
      this.terms = false
      this.v$.$reset()
      // this.reCaptchaResponse =""
    },

    getSelectedSubject(sub) {
      this.selectedSubj = sub

      jQuery('.s-contact-form__row--first').slideUp()
    },
  },
}
</script>
